import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Shield, Download, Trash2, Clock, AlertTriangle, Server, Lock,
  RefreshCw, Eye, FileJson, FileSpreadsheet, CheckCircle2,
  HardDrive, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DataProtectionPanel = () => {
  const { user, token } = useAuth();
  const [_settings, setSettings] = useState(null);
  const [securityInfo, setSecurityInfo] = useState(null);
  const [deletionHistory, setDeletionHistory] = useState([]);
  const [retentionPreview, setRetentionPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [exportFormat, setExportFormat] = useState('json');
  const [deleteReason, setDeleteReason] = useState('');
  const [anonymizeLogs, setAnonymizeLogs] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [retentionMonths, setRetentionMonths] = useState(24);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchSettings(),
      fetchUsers(),
      fetchDeletionHistory(),
      fetchRetentionPreview()
    ]);
    setLoading(false);
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/data-protection/settings`, { headers });
      const data = await response.json();
      setSettings(data.settings);
      setSecurityInfo(data.security_info);
      setRetentionMonths(data.settings?.retention_months || 24);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users?include_inactive=false`, { headers });
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchDeletionHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/data-protection/deletion-history`, { headers });
      const data = await response.json();
      setDeletionHistory(data.deletions || []);
    } catch (err) {
      console.error('Failed to fetch deletion history:', err);
    }
  };

  const fetchRetentionPreview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/data-protection/retention-preview`, { headers });
      const data = await response.json();
      setRetentionPreview(data);
    } catch (err) {
      console.error('Failed to fetch retention preview:', err);
    }
  };

  const handleExportData = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/data-protection/export/${selectedUser}?format=${exportFormat}`,
        { headers }
      );
      const data = await response.json();

      if (exportFormat === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `user-data-export-${selectedUser}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // CSV - download multiple files
        Object.entries(data.files || {}).forEach(([filename, content]) => {
          const blob = new Blob([content], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        });
      }

      toast.success('Data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !deleteReason || deleteReason.length < 10) {
      toast.error('Please select a user and provide a detailed reason (min 10 chars)');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/data-protection/users/${selectedUser}?reason=${encodeURIComponent(deleteReason)}&anonymize_logs=${anonymizeLogs}`,
        { method: 'DELETE', headers }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success('User data deleted and anonymized');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      setDeleteReason('');
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const handleUpdateRetention = async () => {
    try {
      const response = await fetch(`${API_URL}/api/data-protection/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ retention_months: retentionMonths })
      });

      if (!response.ok) throw new Error('Failed to update');

      toast.success('Retention period updated');
      fetchSettings();
      fetchRetentionPreview();
    } catch (err) {
      toast.error('Failed to update retention settings');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  const selectedUserData = users.find(u => u.id === selectedUser);

  return (
    <div className="space-y-6" data-testid="data-protection-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#1e3a5f]" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-[#1e3a5f]">Data Protection</h2>
            <p className="text-slate-500 text-sm">GDPR compliance and data management</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Security Info Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Server className="w-5 h-5 text-emerald-600 mb-2" />
          <h3 className="font-medium text-slate-900 text-sm">Data Hosting</h3>
          <p className="text-slate-500 text-xs mt-1">{securityInfo?.data_hosting?.location || 'EU (London, UK)'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Lock className="w-5 h-5 text-blue-600 mb-2" />
          <h3 className="font-medium text-slate-900 text-sm">Encryption</h3>
          <p className="text-slate-500 text-xs mt-1">{securityInfo?.encryption?.at_rest || 'AES-256'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <HardDrive className="w-5 h-5 text-purple-600 mb-2" />
          <h3 className="font-medium text-slate-900 text-sm">Backups</h3>
          <p className="text-slate-500 text-xs mt-1">{securityInfo?.backups?.frequency || 'Daily'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Activity className="w-5 h-5 text-orange-600 mb-2" />
          <h3 className="font-medium text-slate-900 text-sm">Breach Response</h3>
          <p className="text-slate-500 text-xs mt-1">{securityInfo?.breach_response?.notification || '72h notification'}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Data Export Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Download className="w-5 h-5 text-[#1e3a5f]" />
            <h3 className="font-heading font-semibold text-[#1e3a5f]">Export User Data</h3>
          </div>
          <p className="text-slate-500 text-sm mb-4">
            Export all personal data for a user (GDPR Article 20 - Data Portability)
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Select User</Label>
              <select
                value={selectedUser || ''}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm"
                data-testid="export-user-select"
              >
                <option value="">Choose a user...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-sm font-medium">Export Format</Label>
              <div className="flex gap-3 mt-2">
                <button
                  onClick={() => setExportFormat('json')}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${
                    exportFormat === 'json' 
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' 
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <FileJson className="w-4 h-4" />
                  <span className="text-sm font-medium">JSON</span>
                </button>
                <button
                  onClick={() => setExportFormat('csv')}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${
                    exportFormat === 'csv' 
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' 
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="text-sm font-medium">CSV</span>
                </button>
              </div>
            </div>

            <Button
              onClick={handleExportData}
              disabled={!selectedUser}
              className="w-full bg-[#1e3a5f] hover:bg-[#162c46]"
              data-testid="export-data-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </Button>
          </div>
        </div>

        {/* Data Retention Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-[#1e3a5f]" />
            <h3 className="font-heading font-semibold text-[#1e3a5f]">Data Retention</h3>
          </div>
          <p className="text-slate-500 text-sm mb-4">
            Configure how long data is retained before anonymization
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Retention Period (months)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  min="6"
                  max="120"
                  value={retentionMonths}
                  onChange={(e) => setRetentionMonths(parseInt(e.target.value) || 24)}
                  className="flex-1"
                  data-testid="retention-months-input"
                />
                <Button onClick={handleUpdateRetention} variant="outline">
                  Update
                </Button>
              </div>
            </div>

            {retentionPreview && (
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 text-sm mb-2">Records Beyond Retention</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-[#1e3a5f]">
                      {retentionPreview.affected_records?.archived_enquiries || 0}
                    </p>
                    <p className="text-xs text-slate-500">Enquiries</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1e3a5f]">
                      {retentionPreview.affected_records?.action_logs || 0}
                    </p>
                    <p className="text-xs text-slate-500">Action Logs</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1e3a5f]">
                      {retentionPreview.affected_records?.outreach_logs || 0}
                    </p>
                    <p className="text-xs text-slate-500">Outreach Logs</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete User Data Section */}
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trash2 className="w-5 h-5 text-red-600" />
          <h3 className="font-heading font-semibold text-red-600">Delete User Data</h3>
        </div>
        <p className="text-slate-500 text-sm mb-4">
          Permanently delete a user and their personal data (GDPR Article 17 - Right to Erasure)
        </p>

        {!showDeleteConfirm ? (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Select User to Delete</Label>
              <select
                value={selectedUser || ''}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm"
                data-testid="delete-user-select"
              >
                <option value="">Choose a user...</option>
                {users.filter(u => u.id !== user?.id).map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            {selectedUserData && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Warning</p>
                    <p className="text-amber-700 text-sm mt-1">
                      You are about to delete <strong>{selectedUserData.first_name} {selectedUserData.last_name}</strong> 
                      ({selectedUserData.email}). This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!selectedUser}
              variant="destructive"
              className="w-full"
              data-testid="show-delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Proceed to Delete
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Final Confirmation</p>
                  <p className="text-red-700 text-sm mt-1">
                    Deleting: <strong>{selectedUserData?.email}</strong>
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Reason for Deletion (required)</Label>
              <Input
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g., User requested data deletion under GDPR Article 17"
                className="mt-1"
                data-testid="delete-reason-input"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 10 characters for audit purposes</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="anonymize"
                checked={anonymizeLogs}
                onChange={(e) => setAnonymizeLogs(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="anonymize" className="text-sm text-slate-600">
                Anonymize activity logs (recommended for audit compliance)
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteReason('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteUser}
                disabled={deleteReason.length < 10}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Confirm Delete
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Deletion History */}
      {deletionHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-[#1e3a5f]" />
            <h3 className="font-heading font-semibold text-[#1e3a5f]">Deletion History</h3>
          </div>

          <div className="divide-y divide-slate-100">
            {deletionHistory.map((deletion) => (
              <div key={deletion.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900 text-sm">{deletion.user_name || deletion.user_email}</p>
                  <p className="text-slate-500 text-xs">{deletion.reason}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-xs">
                    {new Date(deletion.created_at).toLocaleDateString('en-GB')}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    {deletion.anonymize_logs ? (
                      <span className="text-xs text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Anonymized
                      </span>
                    ) : (
                      <span className="text-xs text-red-600 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Hard Deleted
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataProtectionPanel;
